import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import utils from "@/utils/utils";
import EventCenter from "@/utils/EventCenter";
import AirwallexDialog from "./AirwallexDialog.vue";
export default {
  __name: 'RechargeDialog',
  props: {
    /** 充值弹窗显隐（:visible.sync 使用） */
    visible: {
      type: Boolean,
      default: false
    },
    /** 当前团队可用余额（右侧明细展示用，由父组件传入） */
    balance: {
      type: Number,
      default: 0
    },
    /** 默认充值金额（打开弹窗时预填，如支付页传入应付金额） */
    defaultAmount: {
      type: Number,
      default: 0
    }
  },
  emits: ["update:visible", "success"],
  setup(__props, {
    emit
  }) {
    const props = __props;
    const {
      proxy
    } = getCurrentInstance();
    const dialogVisible = computed({
      get: () => props.visible,
      set: val => emit("update:visible", val)
    });
    const numberReg = /^\d+(\.\d{1,2})?$/;
    function validateNumber(i18n) {
      return (rule, value, callback) => {
        if (!numberReg.test(value)) {
          callback(new Error(i18n("team.Invalid-Amount")));
        } else if (Number(value) <= 0) {
          callback(new Error(i18n("team.Amount-Greater-Zero")));
        } else {
          callback();
        }
      };
    }

    /** 充值表单数据 */
    const formData = reactive({
      currency: "USD",
      recharge: "",
      recharge_channel: "airwallex"
    });

    // 打开弹窗时预填默认充值金额
    watch(() => props.visible, val => {
      if (val) {
        formData.recharge = props.defaultAmount ? props.defaultAmount.toFixed(2) : "";
      }
    });
    const formRules = {
      currency: {
        required: true,
        message: proxy.$t("team.Select-Currency"),
        trigger: "change"
      },
      recharge: [{
        required: true,
        message: proxy.$t("team.Enter-Recharge-Amount"),
        trigger: "blur"
      }, {
        validator: validateNumber(key => proxy.$t(key)),
        trigger: "blur"
      }]
    };

    /** 充值渠道列表 */
    const rechargeChannels = [{
      label: "Airwallex",
      recharge_rates: 0.001,
      value: "airwallex",
      disabled: false,
      icon: `${utils.imageRoot}/team/airwallex.png`
    }, {
      label: "LianLian",
      value: "Lian",
      disabled: true,
      recharge_rates: 0.03,
      icon: `${utils.imageRoot}/team/LianLian.png`
    }, {
      label: "WLH",
      value: "WLH",
      disabled: true,
      recharge_rates: 0.03,
      icon: `${utils.imageRoot}/team/WLH.png`
    }, {
      label: "PingPong",
      value: "pingPong",
      disabled: true,
      recharge_rates: 0.03,
      icon: `${utils.imageRoot}/team/pingpong.png`
    }];
    const RefDialogRechargeForm = ref(null);
    const currencySelect = ref(null);
    const currencySelectOpened = ref(false);
    const loading = ref(false);

    /** 空中云汇支付弹窗 */
    const airwallexDialogVisible = ref(false);
    const airwallexData = reactive({
      client_secret: "",
      currency: "",
      intent_id: ""
    });

    /**
     * 充值广播
     * @type {BroadcastChannel|null}
     */
    let rechargeBroadChannel = null;

    /**
     * 在线充值当前费率
     */
    const currenteRechargeRate = computed(() => {
      return rechargeChannels.find(item => item.value === formData.recharge_channel)?.recharge_rates * 100;
    });

    /**
     * 当前充值金额
     */
    const currentRechargeNumber = computed(() => {
      if (numberReg.test(formData.recharge)) {
        return utils.toThousandsNew(formData.recharge, 2);
      } else {
        return "0.00";
      }
    });

    /**
     * 当前费率金额
     */
    const currentRechargeRateNumber = computed(() => {
      const recharge = formData.recharge;
      if (!numberReg.test(recharge)) return "0.00";
      const channel = rechargeChannels.find(item => item.value === formData.recharge_channel);
      if (!channel) return "0.00";
      const rateAmount = Number(recharge) * channel.recharge_rates;
      return utils.toThousandsNew(utils.roundHalfEven(rateAmount, 2), 2);
    });

    /**
     * 当前实付金额（充值金额 + 费率金额）
     */
    const currentRecharteActualAmount = computed(() => {
      const recharge = formData.recharge;
      if (!numberReg.test(recharge)) return "0.00";
      const channel = rechargeChannels.find(item => item.value === formData.recharge_channel);
      if (!channel) return "0.00";
      const actualAmount = Number(recharge) * (1 + channel.recharge_rates);
      return utils.toThousandsNew(utils.roundHalfEven(actualAmount, 2), 2);
    });
    onMounted(() => {
      rechargeBroadChannel = new BroadcastChannel(EventCenter.RechargeMessage);
    });

    /**
     * 空中云汇弹窗关闭：清空 intent 数据
     */
    function onAirwallexClose() {
      airwallexData.client_secret = "";
      airwallexData.currency = "";
      airwallexData.intent_id = "";
    }

    /** 空中云汇支付失败回调 */
    function onAirwallexFailed(err) {
      console.error("onAirwallexFailed :>>>", err);
      proxy.$message.error(err?.message || proxy.$t("team.Payment-Component-Failed"));
    }

    /**
     * 关闭所有充值弹窗
     */
    function closeAllDialogs() {
      airwallexDialogVisible.value = false;
      dialogVisible.value = false;
    }

    /** 空中云汇支付成功回调 */
    function onAirwallexSuccess(data) {
      console.debug("onAirwallexSuccess :>>>", data);
      proxy.$axios.get_payment_intent(airwallexData.intent_id).then(res => {
        if (res.code === 1) {
          const status = res.data.status;
          switch (status) {
            case 5:
              // SUCCEEDED
              proxy.$message.success(proxy.$t("team.Recharge-Success-Short"));
              closeAllDialogs();
              emit("success", formData.recharge);
              // 向订阅了 EventCenter.RechargeMessage 事件的窗口发送充值成功的消息
              rechargeBroadChannel.postMessage("SUCCESS");
              break;
            case 4:
              // PENDING
              proxy.$message.info(proxy.$t("team.Recharge-Processing"));
              closeAllDialogs();
              emit("success", formData.recharge);
              rechargeBroadChannel.postMessage("SUCCESS");
              break;
            case 3:
              // REQUIRES_CAPTURE
              proxy.$message.info(proxy.$t("team.Recharge-Accepted"));
              closeAllDialogs();
              emit("success", formData.recharge);
              rechargeBroadChannel.postMessage("SUCCESS");
              break;
            case 2:
              // REQUIRES_CUSTOMER_ACTION
              proxy.$message.warning(proxy.$t("team.Extra-Verification"));
              // 不关闭弹窗，保持 DropIn 可见以让用户完成验证操作
              break;
            case 1:
              // REQUIRES_PAYMENT_METHOD
              proxy.$message.error(proxy.$t("team.Payment-Session-Error"));
              closeAllDialogs();
              break;
            case 6:
              // CANCELLED
              proxy.$message.error(proxy.$t("team.Recharge-Cancelled"));
              closeAllDialogs();
              break;
            default:
              proxy.$message.error(proxy.$t("team.Recharge-Status-Unknown"));
              closeAllDialogs();
          }
        } else {
          proxy.$message.error(res.msg || proxy.$t("team.Query-Status-Failed"));
        }
      });
    }

    /**
     * 开始充值
     */
    async function dialogRechargeConfirm() {
      try {
        loading.value = true;
        await RefDialogRechargeForm.value.validate();
        const res = await proxy.$axios.create_payment(formData.recharge);
        if (res.code === 1) {
          airwallexData.client_secret = res.data.client_secret;
          airwallexData.currency = res.data.currency;
          airwallexData.intent_id = res.data.intent_id;
          airwallexDialogVisible.value = true;
        }
      } finally {
        loading.value = false;
      }
    }

    /**
     * 充值弹窗关闭：重置表单
     */
    function dialogRechargeClose() {
      formData.recharge = "";
      proxy.$nextTick(() => {
        RefDialogRechargeForm.value.clearValidate();
      });
    }
    return {
      __sfc: true,
      proxy,
      props,
      emit,
      dialogVisible,
      numberReg,
      validateNumber,
      formData,
      formRules,
      rechargeChannels,
      RefDialogRechargeForm,
      currencySelect,
      currencySelectOpened,
      loading,
      airwallexDialogVisible,
      airwallexData,
      rechargeBroadChannel,
      currenteRechargeRate,
      currentRechargeNumber,
      currentRechargeRateNumber,
      currentRecharteActualAmount,
      onAirwallexClose,
      onAirwallexFailed,
      closeAllDialogs,
      onAirwallexSuccess,
      dialogRechargeConfirm,
      dialogRechargeClose,
      utils,
      AirwallexDialog
    };
  }
};